import { createFeature, createReducer, on } from '@ngrx/store';
import { UserProfile } from '../models/role.model';
import { ProfileActions } from './profile.actions';
import { ProfilePatch } from '../apis/profile.api';

export const PROFILE_STATUS = {
  idle: 'idle',
  loading: 'loading',
  saving: 'saving',
  ready: 'ready',
  error: 'error',
} as const;

export type ProfileStatus = (typeof PROFILE_STATUS)[keyof typeof PROFILE_STATUS];

export interface ProfileState {
  profile: UserProfile | null;
  status: ProfileStatus;
  error: string | null;
}

const initialState: ProfileState = {
  profile: null,
  status: PROFILE_STATUS.idle,
  error: null,
};

function mergePatch(profile: UserProfile | null, patch: ProfilePatch): UserProfile | null {
  if (!profile) return profile;

  return (({
    ...(profile as unknown as Record<string, unknown>),
    ...(patch as unknown as Record<string, unknown>),
  } as unknown) as UserProfile);
}

const reducer = createReducer(
  initialState,

  on(ProfileActions.loadProfileRequested, (state): ProfileState => ({
    ...state,
    status: PROFILE_STATUS.loading,
    error: null,
  })),

  on(ProfileActions.loadProfileSucceeded, (state, { profile }): ProfileState => ({
    ...state,
    profile,
    status: PROFILE_STATUS.ready,
    error: null,
  })),

  on(ProfileActions.loadProfileFailed, (state, { error }): ProfileState => ({
    ...state,
    status: PROFILE_STATUS.error,
    error,
  })),

  on(ProfileActions.updateProfileRequested, (state): ProfileState => ({
    ...state,
    status: PROFILE_STATUS.saving,
    error: null,
  })),

  on(ProfileActions.updateProfileSucceeded, (state, { patch }): ProfileState => ({
    ...state,
    profile: mergePatch(state.profile, patch),
    status: PROFILE_STATUS.ready,
    error: null,
  })),

  on(ProfileActions.updateProfileFailed, (state, { error }): ProfileState => ({
    ...state,
    status: PROFILE_STATUS.error,
    error,
  })),

  on(ProfileActions.clearProfile, (): ProfileState => ({
    ...initialState,
  }))
);

export const profileFeature = createFeature({
  name: 'profile',
  reducer,
});
